// Geocoding service with caching and fallback strategies
import axios from 'axios';
import { ZipcodeMetrics } from './types';
import { apiUrl } from 'src/constants/config';

interface GeocodeResult {
  lat: number;
  lng: number;
  city?: string;
  state?: string;
}

// Cache for storing geocoded results
const geocodeCache = new Map<string, GeocodeResult | null>();

// Rate limiting for free services
let lastRequestTime = 0;
const RATE_LIMIT_MS = 1000; // 1 second between requests for Nominatim

// Configuration - set your preferred service
const GEOCODING_CONFIG = {
  // Primary service: 'nominatim' (free) | 'opencage' | 'google'
  primary: 'proxy',
  // API keys (set these in your environment)
  opencageApiKey: process.env.REACT_APP_OPENCAGE_API_KEY,
  googleApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  // Enable caching
  useCache: true,
  // Enable localStorage persistence
  usePersistentCache: true,
};

// Load cache from localStorage on startup
const loadCacheFromStorage = (): void => {
  if (!GEOCODING_CONFIG.usePersistentCache) return;

  try {
    const stored = localStorage.getItem('zipcode-geocode-cache');
    if (stored) {
      const parsed = JSON.parse(stored);
      Object.entries(parsed).forEach(([key, value]) => {
        geocodeCache.set(key, value as GeocodeResult | null);
      });
    }
  } catch (error) {
    console.warn('Failed to load geocode cache from localStorage:', error);
  }
};

// Save cache to localStorage
const saveCacheToStorage = (): void => {
  if (!GEOCODING_CONFIG.usePersistentCache) return;

  try {
    const cacheObject = Object.fromEntries(geocodeCache);
    localStorage.setItem('zipcode-geocode-cache', JSON.stringify(cacheObject));
  } catch (error) {
    console.warn('Failed to save geocode cache to localStorage:', error);
  }
};

// Rate limiting helper
const waitForRateLimit = async(): Promise<void> => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (timeSinceLastRequest < RATE_LIMIT_MS) {
    const waitTime = RATE_LIMIT_MS - timeSinceLastRequest;
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }

  lastRequestTime = Date.now();
};

const geocodeWithProxy = async(zipcode: string): Promise<GeocodeResult | null> => {
  try {
    axios.defaults.timeout = 60000;
    const response = await axios.get(`${apiUrl}/geocode/zipcode/${zipcode}`);

    if (response.status !== 200) throw new Error(`HTTP ${response.status}`);

    const data = response.data.item;

    if (data && data.length > 0) {
      const result = data.geocode;
      return {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        city: result.address?.city || result.address?.town || result.address?.village,
        state: result.address?.state,
      };
    }

    return null;
  } catch (error) {
    console.error('Proxy geocoding error:', error);
    return null;
  }

};


// Nominatim (OpenStreetMap) - Free service
const geocodeWithNominatim = async(zipcode: string): Promise<GeocodeResult | null> => {
  await waitForRateLimit();

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=1&countrycodes=us&postalcode=${zipcode}`,
      {
        headers: {
          'User-Agent': 'Survey Dashboard App (contact@yourcompany.com)', // Required by Nominatim
        },
      }
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();

    if (data && data.length > 0) {
      const result = data[0];
      return {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        city: result.address?.city || result.address?.town || result.address?.village,
        state: result.address?.state,
      };
    }

    return null;
  } catch (error) {
    console.error('Nominatim geocoding error:', error);
    return null;
  }
};

// OpenCage Geocoding API
const geocodeWithOpenCage = async(zipcode: string): Promise<GeocodeResult | null> => {
  if (!GEOCODING_CONFIG.opencageApiKey) {
    console.warn('OpenCage API key not configured');
    return null;
  }

  try {
    const response = await fetch(
      `https://api.opencagedata.com/geocode/v1/json?q=${zipcode}&key=${GEOCODING_CONFIG.opencageApiKey}&countrycode=us&limit=1`
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();

    if (data.results && data.results.length > 0) {
      const result = data.results[0];
      return {
        lat: result.geometry.lat,
        lng: result.geometry.lng,
        city: result.components.city || result.components.town,
        state: result.components.state_code,
      };
    }

    return null;
  } catch (error) {
    console.error('OpenCage geocoding error:', error);
    return null;
  }
};

// Google Geocoding API
const geocodeWithGoogle = async(zipcode: string): Promise<GeocodeResult | null> => {
  if (!GEOCODING_CONFIG.googleApiKey) {
    console.warn('Google API key not configured');
    return null;
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${zipcode}&key=${GEOCODING_CONFIG.googleApiKey}&components=country:US`
    );

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();

    if (data.results && data.results.length > 0) {
      const result = data.results[0];
      const location = result.geometry.location;

      // Extract city and state from address components
      const addressComponents = result.address_components;
      const city = addressComponents.find((c: any) => c.types.includes('locality'))?.long_name;
      const state = addressComponents.find((c: any) => c.types.includes('administrative_area_level_1'))?.short_name;

      return {
        lat: location.lat,
        lng: location.lng,
        city,
        state,
      };
    }

    return null;
  } catch (error) {
    console.error('Google geocoding error:', error);
    return null;
  }
};

// Main geocoding function with fallback strategy
export const getZipcodeCoordinates = async(zipcode: string): Promise<GeocodeResult | null> => {
  // Check cache first
  if (GEOCODING_CONFIG.useCache && geocodeCache.has(zipcode)) {
    return geocodeCache.get(zipcode) || null;
  }

  // Try primary service
  let result: GeocodeResult | null = null;

  switch (GEOCODING_CONFIG.primary) {
    case 'proxy':
      result = await geocodeWithProxy(zipcode);
      break;
    case 'nominatim':
      result = await geocodeWithNominatim(zipcode);
      break;
    case 'opencage':
      result = await geocodeWithOpenCage(zipcode);
      break;
    case 'google':
      result = await geocodeWithGoogle(zipcode);
      break;
  }

  // Fallback to other services if primary fails
  if (!result && GEOCODING_CONFIG.primary !== 'nominatim') {
    result = await geocodeWithNominatim(zipcode);
  }

  // Cache the result (even if null to avoid repeated failed requests)
  if (GEOCODING_CONFIG.useCache) {
    geocodeCache.set(zipcode, result);
    saveCacheToStorage();
  }

  return result;
};

// Batch geocoding with progress tracking
export const batchGeocodeZipcodes = async(
  zipcodes: string[],
  onProgress?: (completed: number, total: number) => void
): Promise<Map<string, GeocodeResult | null>> => {
  const results = new Map<string, GeocodeResult | null>();

  for (let i = 0; i < zipcodes.length; i++) {
    const zipcode = zipcodes[i];
    const result = await getZipcodeCoordinates(zipcode);
    results.set(zipcode, result);

    onProgress?.(i + 1, zipcodes.length);

    // Small delay to be respectful to free services
    if (GEOCODING_CONFIG.primary === 'nominatim' && i < zipcodes.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  return results;
};

// Enhanced processing function for zipcode data
export const processZipcodeData = async(
  data: ZipcodeMetrics[],
  onProgress?: (completed: number, total: number) => void
): Promise<ZipcodeMetrics[]> => {
  // Load cache on first use
  if (geocodeCache.size === 0) {
    loadCacheFromStorage();
  }

  const processedData = await Promise.all(
    data.map(async(zipcode, index) => {
      if (zipcode.lat && zipcode.lng) {
        return zipcode; // Already has coordinates
      }

      const coordinates = await getZipcodeCoordinates(zipcode.zipcode);

      // Call progress callback
      onProgress?.(index + 1, data.length);

      return {
        ...zipcode,
        lat: coordinates?.lat,
        lng: coordinates?.lng,
        city: coordinates?.city,
        state: coordinates?.state,
      };
    })
  );

  // Filter out zipcodes without coordinates
  return processedData.filter(zipcode => zipcode.lat && zipcode.lng);
};

// Utility to clear cache
export const clearGeocodeCache = (): void => {
  geocodeCache.clear();
  if (GEOCODING_CONFIG.usePersistentCache) {
    localStorage.removeItem('zipcode-geocode-cache');
  }
};

// Utility to get cache stats
export const getGeocodeStats = () => {
  return {
    cacheSize: geocodeCache.size,
    cached: Array.from(geocodeCache.keys()),
  };
};
